import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "@/core/lib/api";

export type SignInMethods = {
  providers: string[];
  hasPassword: boolean;
};

export type OwnedCompanyCandidate = {
  userId: string;
  name: string;
  email: string;
};

export type OwnedCompanySummary = {
  id: string;
  name: string;
  candidates: OwnedCompanyCandidate[];
};

class AccountApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new AccountApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new AccountApiError(message, res.status);
  }
  return data as T;
}

export async function getSignInMethods(): Promise<SignInMethods> {
  return request<SignInMethods>("/api/account/sign-in-methods");
}

export async function getOwnedCompanies(): Promise<OwnedCompanySummary[]> {
  const res = await request<{ items: OwnedCompanySummary[] }>("/api/account/owned-companies");
  return res.items;
}

export async function transferOwnedCompany(input: {
  companyId: string;
  userId: string;
}): Promise<void> {
  await request<{ ok: true }>(`/api/account/companies/${input.companyId}/transfer`, {
    method: "POST",
    body: JSON.stringify({ userId: input.userId }),
  });
}

export async function setPasswordApi(newPassword: string): Promise<void> {
  await request<{ ok: true }>("/api/account/set-password", {
    method: "POST",
    body: JSON.stringify({ newPassword }),
  });
}

/**
 * Uploads an avatar image to the backend, which stores it in R2 and updates the
 * user record. Returns the public URL of the stored image.
 *
 * Note: we don't set Content-Type — the browser must set the multipart boundary
 * itself — so this can't reuse the JSON `request` helper.
 */
export async function uploadAvatar(file: File): Promise<{ image: string }> {
  const body = new FormData();
  body.append("file", file);

  let res: Response;
  try {
    res = await apiFetch(apiUrl("/api/account/avatar"), {
      method: "POST",
      credentials: "include",
      body,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new AccountApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Upload failed (${res.status})`;
    throw new AccountApiError(message, res.status);
  }
  return data as { image: string };
}

export { AccountApiError };
